import React from 'react';
import { useAudioRecognition } from '../AudioRecognitionContext';
import type { AudioRecExpectedEvent, AudioRecRecognitionHints } from '../types';

export interface ExpectedMidiHintSnapshot {
  midiNotes: number[];
  expectationKey?: string;
  events?: AudioRecExpectedEvent[];
}

interface UseExpectedMidiHintsOptions {
  enabled?: boolean;
  getExpectedMidiSnapshot: () => ExpectedMidiHintSnapshot;
  mode?: AudioRecRecognitionHints['mode'];
  pollMs?: number;
}

export const AUDIO_RECOGNITION_HINT_POLL_MS = 40;

const normalizeExpectedMidiNotes = (notes: number[]) =>
  Array.from(new Set(notes.map((note) => Math.round(note))))
    .filter((note) => note >= 0 && note <= 127)
    .sort((a, b) => a - b);

const buildHintSignature = (hints: AudioRecRecognitionHints | null) =>
  hints
    ? [
        hints.mode ?? 'default',
        hints.expectationKey ?? '',
        (hints.expectedMidiNotes ?? []).join(','),
        (hints.expectedEvents ?? []).map((event) => `${event.id}:${event.phase}`).join(','),
      ].join(':')
    : 'null';

const normalizeExpectedEvents = (events: AudioRecExpectedEvent[] | undefined) =>
  (events ?? [])
    .filter(
      (event) =>
        event != null &&
        typeof event.id === 'string' &&
        Number.isFinite(event.midiNote) &&
        Number.isFinite(event.onsetTimestamp) &&
        Number.isFinite(event.offsetTimestamp),
    )
    .map((event) => ({ ...event, midiNote: Math.round(event.midiNote) }))
    .filter((event) => event.midiNote >= 0 && event.midiNote <= 127)
    .sort(
      (left, right) =>
        left.onsetTimestamp - right.onsetTimestamp ||
        left.midiNote - right.midiNote ||
        left.id.localeCompare(right.id),
    );

export const useExpectedMidiHints = ({
  enabled = true,
  getExpectedMidiSnapshot,
  mode = 'exercise',
  pollMs = AUDIO_RECOGNITION_HINT_POLL_MS,
}: UseExpectedMidiHintsOptions) => {
  const { setRecognitionHints } = useAudioRecognition();
  const getExpectedMidiSnapshotRef = React.useRef(getExpectedMidiSnapshot);
  const lastSignatureRef = React.useRef<string | null>(null);
  const didWarnHintReadRef = React.useRef(false);

  getExpectedMidiSnapshotRef.current = getExpectedMidiSnapshot;

  React.useEffect(() => {
    if (!enabled) {
      lastSignatureRef.current = null;
      didWarnHintReadRef.current = false;
      setRecognitionHints(null);
      return;
    }

    lastSignatureRef.current = '__init__';

    const pushHints = () => {
      let expectedSnapshot: ExpectedMidiHintSnapshot = { midiNotes: [] };

      try {
        expectedSnapshot = getExpectedMidiSnapshotRef.current();
        didWarnHintReadRef.current = false;
      } catch (error) {
        if (!didWarnHintReadRef.current) {
          console.warn(
            '[AudioRecognition] Failed to resolve expected MIDI notes for hints.',
            error,
          );
          didWarnHintReadRef.current = true;
        }
      }

      const expectedMidiNotes = normalizeExpectedMidiNotes(expectedSnapshot.midiNotes);
      const expectedEvents = normalizeExpectedEvents(expectedSnapshot.events);

      const hints =
        expectedMidiNotes.length > 0
          ? {
              mode,
              expectedMidiNotes,
              expectationKey: expectedSnapshot.expectationKey ?? '',
              expectedEvents,
            }
          : null;
      const signature = buildHintSignature(hints);
      if (signature === lastSignatureRef.current) return;
      lastSignatureRef.current = signature;
      setRecognitionHints(hints);
    };

    pushHints();
    const intervalId = window.setInterval(pushHints, Math.max(40, pollMs));

    return () => {
      window.clearInterval(intervalId);
      lastSignatureRef.current = null;
      didWarnHintReadRef.current = false;
      setRecognitionHints(null);
    };
  }, [enabled, mode, pollMs, setRecognitionHints]);
};
